/**
 * The action menu on a board card, kept small because there are a lot of them.
 *
 * A `<flux:dropdown>` per card was 84% of the board's HTML — six kilobytes of chrome, inline SVG
 * and nested markup, drawn for every card whether or not anybody ever opened one. On a 300-item
 * board that is megabytes of DOM, re-sent and re-morphed on every click. This is the same menu in
 * about a tenth of the markup: a button, and a list that is `hidden` until it is wanted.
 *
 * `position: fixed`, placed from the trigger's own rect at open time. Fixed escapes the column's
 * overflow without needing to teleport anywhere. (Under a drag the card DOES carry a transform,
 * which is exactly when the menu must not be open; opening closes any drag in progress by
 * definition.)
 *
 * ── The viewport is not always the containing block ─────────────────────────────────────────────
 *
 * `position: fixed` resolves against the nearest ancestor that establishes a containing block,
 * and a transform is not the only thing that does: `filter`, `backdrop-filter`, `perspective`,
 * `will-change` and `contain` all do too. The list view's table shell carries `backdrop-blur` in
 * dark mode — so the menu's viewport coordinates were being read relative to that wrapper, which
 * put it far down and to the right, clipped by the table's own `overflow-x`.
 *
 * The board has no such ancestor, which is why only the list view showed it. Rather than forbid a
 * design somewhere else in the application, the offset is measured and subtracted.
 *
 * Closes on: another menu opening, a click anywhere else, Escape, scroll, resize. A menu that
 * survives the board scrolling underneath it is a menu floating over the wrong card.
 */
const OPEN_EVENT = 'zar-card-menu-open';

/**
 * A serial number per menu, because object identity is not usable here.
 *
 * Alpine hands methods a REACTIVE PROXY of the component rather than the component, and the proxy
 * you are given is not guaranteed to be the same object every time — so "was this event mine?"
 * asked as `event.detail !== this` can answer wrongly, and a menu closes itself the instant it
 * opens. Nothing errors; it simply never appears. A number is a number.
 */
let serial = 0;

/**
 * The nearest ancestor a `position: fixed` child would actually be measured from.
 *
 * Null means the viewport, which is the ordinary case. Anything else is an element that has
 * quietly become the containing block — and the list of properties that do that is longer than
 * most people carry in their head, which is why this is a function rather than a comment saying
 * "don't put a transform above the menu".
 *
 * @see https://developer.mozilla.org/docs/Web/CSS/position#fixed
 */
export function containingBlock(element) {
    let node = element?.parentElement ?? null;

    while (node && node !== document.body && node !== document.documentElement) {
        const style = getComputedStyle(node);

        const creates =
            (style.transform && style.transform !== 'none') ||
            (style.perspective && style.perspective !== 'none') ||
            (style.filter && style.filter !== 'none') ||
            // Safari still answers on the prefixed property alone.
            (style.backdropFilter && style.backdropFilter !== 'none') ||
            (style.webkitBackdropFilter && style.webkitBackdropFilter !== 'none') ||
            /transform|perspective|filter/.test(style.willChange || '') ||
            /paint|layout|strict|content/.test(style.contain || '');

        if (creates) {
            return node;
        }

        node = node.parentElement;
    }

    return null;
}

export function cardMenu() {
    return {
        id: ++serial,
        open: false,
        top: 0,
        left: 0,

        /** The trigger, captured once — `$el` is whatever is evaluating at the time. */
        root: null,

        init() {
            this.root = this.$el;

            // One at a time. Without this every menu ever opened stays open behind the next.
            this._others = (event) => {
                if (event.detail !== this.id && this.open) {
                    this.open = false;
                }
            };
            window.addEventListener(OPEN_EVENT, this._others);
        },

        destroy() {
            window.removeEventListener(OPEN_EVENT, this._others);
        },

        toggle() {
            this.open ? this.close() : this.show();
        },

        show() {
            const trigger = this.root?.querySelector('[data-card-menu-trigger]');
            const rect = trigger ? trigger.getBoundingClientRect() : null;

            if (rect) {
                // Right-aligned to the trigger, and flipped above it when there is no room
                // below — a menu that opens off the bottom of the window cannot be read.
                const height = 260;
                const below = window.innerHeight - rect.bottom;

                let top = below < height && rect.top > height ? rect.top - height : rect.bottom + 4;
                let left = Math.max(8, rect.right - 190);

                // Whatever `fixed` will actually be measured from. Usually the viewport, and
                // then this is a no-op.
                const origin = containingBlock(this.root);

                if (origin) {
                    const box = origin.getBoundingClientRect();
                    top -= box.top;
                    left -= box.left;
                }

                this.top = top;
                this.left = left;
            }

            this.open = true;
            window.dispatchEvent(new CustomEvent(OPEN_EVENT, { detail: this.id }));
        },

        close() {
            this.open = false;
        },
    };
}
